import Vue from 'vue'

export class Node extends Object {
	fetched = false
	_counter = null
	_is_surveyed = null
	_is_root = null
	themes = []
	_folders = []
	_documents = []
	_versions = []
	structure = null

	static isFolderNode(node) {
		return node && node.hasOwnProperty('type') && node.type == 'folder'
	}
	static isDocumentNode(node) {
		return node && node.hasOwnProperty('type') && node.type != 'folder'
	}
	static isRootNode(node) {
		return node && node.hasOwnProperty('folder_id') && node.folder_id == null
	}
	static vendorIdOf(node) {
		return node?.vendor ? node.vendor.id : node?.vendor_id
	}
	static getComputedKeys(instance) {
		const keys = new Set()
		let prototype = Object.getPrototypeOf(instance)
		while (prototype && prototype !== Object.prototype) {
			Object.entries(Object.getOwnPropertyDescriptors(prototype)).forEach(([key, descriptor]) => {
				if (descriptor.get && !descriptor.set) {
					keys.add(key)
				}
			})
			prototype = Object.getPrototypeOf(prototype)
		}
		return keys
	}
	static sanitizeRawNode(instance, rawNode) {
		let sanitized = rawNode

		if (rawNode) {
			sanitized = { ...rawNode }
			Node.getComputedKeys(instance).forEach(key => delete sanitized[key])
		}

		return sanitized
	}
	constructor(rawNode) {
		super()
		this.structure = rawNode?.structure
		Object.assign(this, Node.sanitizeRawNode(this, rawNode))
		if (!this.alias) {
			this.alias = this.hash
		}
		if (rawNode) {
			this.fetched = true
		}
	}
	get is_deleted() {
		return this.hasOwnProperty('deleted_at') && this.deleted_at != null
	}
	get is_folder() {
		return Node.isFolderNode(this)
	}
	get is_document() {
		return Node.isDocumentNode(this)
	}
	get is_root() {
		return this._is_root ? this._is_root : Node.isRootNode(this)
	}
	set is_root(is_root) {
		this._is_root = is_root
	}
	get parent() {
		let result = null
		if (this.structure) {
			let folderId = this.folder_id
			if (this.deleted_at) {
				folderId = 'trash'
			}
			if (folderId) {
				result = this.structure.findNode(folderId)
			}
		}
		return result
	}
	get fullpath() {
		let result = ''
		if (this.parent) {
			result += this.parent.fullpath
		}
		result += '/' + this.name
		return result
	}
	get is_surveyed() {
		return this._is_surveyed
	}
	set is_surveyed(isSurveyed) {
		this._is_surveyed = isSurveyed
	}
	get counter() {
		return this._counter
	}
	set counter(value) {
		if (this._counter !== value) {
			const shouldUpdateParentCounter = this._counter !== null && this.parent
			this._counter = value
			if (shouldUpdateParentCounter) {
				this.parent.updateCounter()
			}
		}
	}
	addOrUpdateProperty(propertyKey, propertyValue) {
		if (!this.hasOwnProperty(propertyKey)) {
			Vue.set(this, propertyKey, propertyValue)
		} else if (
			typeof propertyValue === 'object' &&
			propertyValue !== null &&
			propertyValue !== undefined &&
			typeof this[propertyKey] === 'object' &&
			this[propertyKey] !== null &&
			this[propertyKey] !== undefined
		) {
			Object.assign(this[propertyKey], propertyValue)
		} else {
			this[propertyKey] = propertyValue
		}
	}
	resetcounter() {
		if (Node.isFolderNode(this)) {
			this.folders?.forEach(folder => {
				folder.resetcounter()
			})
			this.documents?.forEach(document => {
				document.resetcounter()
			})
		}
		this.counter = 0
	}
	update(rawNode) {
		let result = null
		const rawNodeVendorId = Node.vendorIdOf(rawNode)
		const thisVendorId = Node.vendorIdOf(this)
		if (rawNode && (!thisVendorId || rawNodeVendorId == thisVendorId)) {
			const isSameNode = this.id == rawNode.id || this.hash == rawNode.hash || this.alias == rawNode.alias
			if (isSameNode && (this.folder_id == rawNode.folder_id || rawNode.folder_id == undefined || this.folder_id == -1 || this.folder_id == undefined)) {
				Object.assign(this, Node.sanitizeRawNode(this, rawNode))
				result = this
			} else if (isSameNode) {
				const that = this
				this.parent?.remove(this)
				const newParent = this.structure?.findNode(rawNode.folder_id)
				Object.assign(that, Node.sanitizeRawNode(that, rawNode))
				const existingNode = newParent?.children.find(child => child.id == that.id)
				if (!existingNode) {
					newParent.add(that)
				} else {
					existingNode.update(that)
				}
				result = that
			}
			this.fetched = true
		}
		return result
	}
}
